<template>
	<div class="ks-autocomplete" :class="containerClasses">
		<label v-if="label" class="ks-autocomplete-label" :class="{ 'ks-autocomplete-label-active': isLabelActive }">{{ label }}</label>
		<k-menu
			v-model="isMenuOpen"
			offset-y
			full-width
			:close-on-content-click="false"
			:disabled="disabled || readonly"
			:max-height="menuMaxHeight"
		>
			<template v-slot:activator>
				<div class="ks-autocomplete-slot" @click="onActivatorClick">
					<div v-if="multiple && selectedItems.length" class="ks-autocomplete-chips">
						<k-chip
							v-for="(item, index) in selectedItems"
							:key="getItemKey(item, index)"
							size="s"
							color="primary"
							:close="!disabled && !readonly"
							@click:close="removeItem(item)"
						>{{ getItemText(item) }}</k-chip>
					</div>
					<k-text-field
						ref="input"
						class="ks-autocomplete-input"
						:value="searchText"
						:disabled="disabled"
						:readonly="readonly"
						:placeholder="computedPlaceholder"
						:prepend-icon="prependIcon"
						:append-icon="isMenuOpen ? 'arrow_drop_up' : 'arrow_drop_down'"
						:clearable="clearable && hasValue"
						:dense="dense"
						:solo="solo"
						:outlined="outlined"
						hide-details
						@input="onInput"
						@focus="onFocus"
						@blur="onBlur"
						@click:prepend="$emit('click:prepend')"
						@click:append="onActivatorClick"
						@click:clear="clear"
						@keydown.down.native.prevent="onKeyDown"
						@keydown.up.native.prevent="onKeyUp"
						@keydown-enter="onKeyEnter"
						@keydown.esc.native="closeMenu"
					/>
				</div>
			</template>

			<div class="ks-autocomplete-menu-content" ref="menuContent" @mousedown="onMenuMouseDown">
				<div v-if="filteredItems.length > 0">
					<div
						v-for="(item, index) in filteredItems"
						:key="getItemKey(item, index)"
						class="ks-autocomplete-item"
						:class="{ 'ks-autocomplete-item-active': isItemSelected(item), 'ks-autocomplete-item-highlighted': index === highlightedIndex }"
						@mousedown.prevent.stop="selectItem(item)"
						@mouseenter="highlightedIndex = index"
					>
						<slot name="item" :item="item">
							<span>{{ getItemText(item) }}</span>
						</slot>
					</div>
				</div>
				<div v-else-if="!hideNoData && searchText && noDataText" class="ks-autocomplete-no-data">
					<slot name="no-data">{{ noDataText }}</slot>
				</div>
			</div>
		</k-menu>
		<div v-if="hint && (persistentHint || isFocused)" class="ks-autocomplete-hint">{{ hint }}</div>
		<div v-if="errorMessages" class="ks-autocomplete-error">{{ errorMessages }}</div>
	</div>
</template>

<script>
export default {
	props: {
		"value": {
			type: [String, Number, Boolean, Object, Array],
			default: null
		},
		"items": {
			type: Array,
			default: () => []
		},
		"itemText": {
			type: [String, Function],
			default: "text"
		},
		"itemValue": {
			type: [String, Function],
			default: "value"
		},
		"label": {
			type: String,
			default: ""
		},
		"placeholder": {
			type: String,
			default: ""
		},
		"disabled": {
			type: Boolean,
			default: false
		},
		"readonly": {
			type: Boolean,
			default: false
		},
		"clearable": {
			type: Boolean,
			default: false
		},
		"dense": {
			type: Boolean,
			default: false
		},
		"solo": {
			type: Boolean,
			default: false
		},
		"outlined": {
			type: Boolean,
			default: false
		},
		"hideDetails": {
			type: Boolean,
			default: false
		},
		"hint": {
			type: String,
			default: ""
		},
		"persistentHint": {
			type: Boolean,
			default: false
		},
		"errorMessages": {
			type: String,
			default: ""
		},
		"prependIcon": {
			type: String,
			default: ""
		},
		"noDataText": {
			type: String,
			default: "No data available"
		},
		"searchInput": {
			type: String,
			default: null
		},
		"returnObject": {
			type: Boolean,
			default: false
		},
		"multiple": {
			type: Boolean,
			default: false
		},
		"chips": {
			type: Boolean,
			default: false
		},
		"deletableChips": {
			type: Boolean,
			default: false
		},
		"filter": {
			type: Function,
			default: null
		},
		"menuMaxHeight": {
			type: [Number, String],
			default: 300
		},
		"hideNoData": {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			searchText: "",
			isFocused: false,
			isMenuOpen: false,
			highlightedIndex: -1,
			internalValue: this.value,
			isInteractingWithMenu: false
		}
	},
	watch: {
		value (val) {
			this.internalValue = val
			if (!this.isFocused) {
				this.searchText = this.getSelectedText()
			}
		},
		items () {
			if (!this.isFocused) {
				this.searchText = this.getSelectedText()
			}
		},
		searchInput (val) {
			if (val !== null) {
				this.searchText = val
			}
		},
		searchText (val) {
			this.$emit("update:search-input", val)
		},
		isMenuOpen (val) {
			if (val) this.resetHighlight()
		},
		filteredItems () {
			if (this.isMenuOpen) this.resetHighlight()
		}
	},
	created () {
		this.searchText = this.getSelectedText()
	},
	computed: {
		"hasValue" () {
			if (this.multiple) {
				return Array.isArray(this.internalValue) && this.internalValue.length > 0
			}
			return this.internalValue !== null && this.internalValue !== undefined && this.internalValue !== ""
		},
		"isLabelActive" () {
			return this.isFocused || this.hasValue || !!this.searchText || !!this.placeholder
		},
		"computedPlaceholder" () {
			if (this.hasValue && !this.isFocused) return ""
			return this.placeholder
		},
		"containerClasses" () {
			return {
				"ks-autocomplete-focused": this.isFocused,
				"ks-autocomplete-disabled": this.disabled,
				"ks-autocomplete-has-error": !!this.errorMessages,
				"ks-autocomplete-solo": this.solo,
				"ks-autocomplete-outlined": this.outlined,
				"ks-autocomplete-dense": this.dense,
				"ks-autocomplete-hide-details": this.hideDetails,
				"ks-autocomplete-multiple": this.multiple
			}
		},
		"filteredItems" () {
			if (!this.searchText || this.searchText === this.getSelectedText()) {
				return this.items
			}
			var search = this.searchText.toLowerCase()
			return this.items.filter(item => {
				if (this.filter) {
					return this.filter(item, search, this.getItemText(item))
				}
				return this.getItemText(item).toString().toLowerCase().indexOf(search) > -1
			})
		},
		"selectedItems" () {
			if (!this.multiple) return []
			if (!Array.isArray(this.internalValue)) return []
			return this.internalValue.map(val => {
				return this.items.find(item => {
					if (this.returnObject) return this.isSameObject(item, val)
					return this.getItemValue(item) === val
				})
			}).filter(Boolean)
		}
	},
	methods: {
		getItemText (item) {
			if (item === null || item === undefined) return ""
			if (typeof item === "string" || typeof item === "number") return String(item)
			if (typeof this.itemText === "function") return this.itemText(item)
			return item[this.itemText] || ""
		},
		getItemValue (item) {
			if (item === null || item === undefined) return ""
			if (typeof item === "string" || typeof item === "number") return item
			if (typeof this.itemValue === "function") return this.itemValue(item)
			return item[this.itemValue] !== undefined ? item[this.itemValue] : item
		},
		getItemKey (item, index) {
    	if (item !== null && item !== undefined && item.id !== undefined && item.id !== null) return item.id;
    	var val = this.getItemValue(item);
    	return index + "-" + (val !== null && typeof val === "object" ? "" : val);
},
		isSameObject (a, b) {
			return JSON.stringify(this.getItemValue(a)) === JSON.stringify(this.getItemValue(b))
		},
		getSelectedText () {
			if (this.multiple) return ""
			if (!this.hasValue) return ""
			var selectedItem = this.items.find(item => {
				if (this.returnObject) return this.isSameObject(item, this.internalValue)
				return this.getItemValue(item) === this.internalValue
			})
			if (selectedItem) return this.getItemText(selectedItem)
			if (typeof this.internalValue === "string" || typeof this.internalValue === "number") return String(this.internalValue)
			return ""
		},
		isItemSelected (item) {
			var itemVal = this.getItemValue(item)
			if (this.multiple) {
				if (!Array.isArray(this.internalValue)) return false
				return this.internalValue.some(v => {
					if (this.returnObject) return this.isSameObject(v, item)
					return v === itemVal
				})
			}
			if (this.returnObject) return this.isSameObject(this.internalValue, item)
			return this.internalValue === itemVal
		},
		selectItem (item) {
			if (this.multiple) {
				var arr = Array.isArray(this.internalValue) ? [...this.internalValue] : []
				var itemVal = this.returnObject ? item : this.getItemValue(item)
				var idx = arr.findIndex(v => {
					if (this.returnObject) return this.isSameObject(v, item)
					return v === this.getItemValue(item)
				})
				if (idx > -1) {
					arr.splice(idx, 1)
				} else {
					arr.push(itemVal)
				}
				this.internalValue = arr
				this.$emit("input", arr)
				this.searchText = ""
				this.$nextTick(() => {
					if (this.$refs.input) this.$refs.input.focus()
				})
			} else {
				var val = this.returnObject ? item : this.getItemValue(item)
				this.internalValue = val
				this.searchText = this.getItemText(item)
				this.$emit("input", val)
				this.closeMenu()
			}
			this.$emit("change", this.internalValue)
		},
		removeItem (item) {
			if (!this.multiple) return
			var arr = Array.isArray(this.internalValue) ? [...this.internalValue] : []
			var idx = arr.findIndex(v => {
				if (this.returnObject) return this.isSameObject(v, item)
				return v === this.getItemValue(item)
			})
			if (idx > -1) {
				arr.splice(idx, 1)
				this.internalValue = arr
				this.$emit("input", arr)
				this.$emit("change", arr)
			}
		},
		onInput (val) {
			this.searchText = val
			if (!this.isMenuOpen) {
				this.openMenu()
			}
			this.resetHighlight()
			this.$emit("update:search-input", this.searchText)
		},
		onFocus (e) {
			this.isFocused = true
			if (!this.multiple && this.hasValue) {
				this.searchText = ""
			}
			this.$emit("focus", e)
		},
		onBlur (e) {
			setTimeout(() => {
				if (this.isInteractingWithMenu) {
					this.isInteractingWithMenu = false
					return
				}
				if (!this.$el.contains(document.activeElement)) {
					this.isFocused = false
					if (!this.multiple) {
						this.searchText = this.getSelectedText()
					}
					this.closeMenu()
					this.$emit("blur", e)
				}
			}, 150)
		},
		onActivatorClick () {
			if (this.disabled || this.readonly) return
			if (this.$refs.input) this.$refs.input.focus()
		},
		openMenu () {
			if (this.disabled || this.readonly) return
			this.isMenuOpen = true
		},
		closeMenu () {
			this.isMenuOpen = false
			this.highlightedIndex = -1
		},
		onKeyDown () {
			if (!this.isMenuOpen) {
				this.openMenu()
				this.resetHighlight()
				return
			}
			if (this.highlightedIndex < this.filteredItems.length - 1) {
				this.highlightedIndex++
			}
			this.scrollToHighlighted()
		},
		onKeyUp () {
			if (this.highlightedIndex > 0) {
				this.highlightedIndex--
			}
			this.scrollToHighlighted()
		},
		onKeyEnter () {
			if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredItems.length) {
				this.selectItem(this.filteredItems[this.highlightedIndex])
			}
		},
		resetHighlight () {
			var selectedIndex = this.multiple ? -1 : this.filteredItems.findIndex(item => this.isItemSelected(item))
			if (selectedIndex > -1) {
				this.highlightedIndex = selectedIndex
			} else {
				this.highlightedIndex = this.filteredItems.length > 0 ? 0 : -1
			}
			this.scrollToHighlighted()
		},
		onMenuMouseDown () {
			this.isInteractingWithMenu = true
		},
		scrollToHighlighted () {
			this.$nextTick(() => {
				if (!this.$refs.menuContent) return
				var items = this.$refs.menuContent.querySelectorAll(".ks-autocomplete-item")
				if (items[this.highlightedIndex]) {
					items[this.highlightedIndex].scrollIntoView({ block: "nearest" })
				}
			})
		},
		clear () {
			if (this.multiple) {
				this.internalValue = []
				this.$emit("input", [])
			} else {
				this.internalValue = null
				this.$emit("input", null)
			}
			this.searchText = ""
			this.$emit("change", this.internalValue)
			this.$emit("click:clear")
			this.$nextTick(() => {
				if (this.$refs.input) this.$refs.input.focus()
			})
		},
		focus () {
			if (this.$refs.input) this.$refs.input.focus()
		},
		blur () {
			if (this.$refs.input) this.$refs.input.blur()
		}
	}
}
</script>

<style>
.ks-autocomplete {
	position: relative;
	margin-bottom: 8px;
}

.ks-autocomplete > .ks-menu {
	display: block;
	width: 100%;
}

.ks-autocomplete-hide-details {
	margin-bottom: 0;
}

.ks-autocomplete-label {
	display: block;
	font-size: 12px;
	color: var(--ks-font-color-input-label);
	margin-bottom: 4px;
	height: 1em;
	transition: color 0.15s ease;
}
.ks-autocomplete-focused .ks-autocomplete-label {
	color: var(--ks-col-accent);
}
.ks-autocomplete-has-error .ks-autocomplete-label {
	color: var(--ks-col-danger);
}

.ks-autocomplete-slot {
	width: 100%;
}

.ks-autocomplete-input {
	width: 100%;
}

.ks-autocomplete-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	padding: 2px 0;
}

.ks-autocomplete-menu-content {
	max-height: inherit;
	overflow-y: auto;
	border-radius: 6px;
	min-width: 220px;
}

.ks-autocomplete-item {
	padding: 8px 12px;
	font-size: 13px;
	color: var(--ks-font-color-2);
	cursor: pointer;
	transition: background 0.1s ease;
}
.ks-autocomplete-item:hover,
.ks-autocomplete-item-highlighted {
	background: var(--ks-background-color-light-op60);
	color: var(--ks-font-color);
}
.ks-autocomplete-item-active {
	color: var(--ks-col-accent);
}
.ks-autocomplete-item-active:hover,
.ks-autocomplete-item-active.ks-autocomplete-item-highlighted {
	background: var(--ks-background-color-light-op60);
	color: var(--ks-col-accent);
}

.ks-autocomplete-no-data {
	padding: 8px 12px;
	font-size: 13px;
	color: var(--ks-font-color-4);
}

/* Hint & Error */
.ks-autocomplete-hint {
	font-size: 11px;
	color: var(--ks-font-color-4);
	margin-top: 4px;
}

.ks-autocomplete-error {
	font-size: 11px;
	color: var(--ks-col-danger);
	margin-top: 4px;
}

/* Solo variant */
.ks-autocomplete-solo .ks-autocomplete-slot {
	border-bottom: none;
}
.ks-autocomplete-solo .ks-autocomplete-slot:hover {
	border-bottom: none;
}
.ks-autocomplete-solo .ks-input-field {
	background: var(--ks-grey-4);
	border: 1px solid transparent;
	border-bottom: 1px solid transparent;
	border-radius: 4px;
	padding: 8px 12px;
}
.ks-autocomplete-solo .ks-input-field:focus {
	border: 1px solid var(--ks-col-accent);
	border-bottom: 1px solid var(--ks-col-accent);
}

/* Outlined variant */
.ks-autocomplete-outlined .ks-autocomplete-slot {
	border-bottom: none;
}
.ks-autocomplete-outlined .ks-autocomplete-slot:hover {
	border-bottom: none;
}
.ks-autocomplete-outlined .ks-input-field {
	background: transparent;
	border: 1px solid var(--ks-grey-4);
	border-bottom: 1px solid var(--ks-grey-4);
	border-radius: 4px;
}
.ks-autocomplete-outlined .ks-input-field:focus {
	border: 1px solid var(--ks-col-accent);
	border-bottom: 1px solid var(--ks-col-accent);
}

/* Dense */
.ks-autocomplete-dense {
	margin-bottom: 4px;
}
.ks-autocomplete-dense .ks-autocomplete-item {
	padding: 5px 10px;
	font-size: 12px;
}

/* Disabled */
.ks-autocomplete-disabled {
	opacity: 0.5;
	pointer-events: none;
}
</style>
